import { addDays, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';

import { getMarketHours } from '~/components/price-display-view/helpers/price-display-helpers';
import { usePriceHistory } from '~/react-query-hooks/prices/usePriceHistory';
import { Tracking } from '~/services/tracking';

import { useMarketReactionDates } from '../use-market-reaction-dates/useMarketReactionDates';
import { getCatalystData } from './getCatalystData';
import { getEarningsData } from './getEarningsData';
import { DATE_FORMAT, PriceByDay } from './types';

export { type PriceByDay } from './types';

export const NEW_YORK_TIMEZONE = 'America/New_York';

interface UsePriceMarketReactionDataProps {
  entityTag: string;
  ticker: string;
  reportingDate: string;
  enabled?: boolean;
  isMultipleEntitiesChart?: boolean;
  variant?: 'earnings' | 'catalyst';
  isMarketCatalyst?: boolean;
}

export const usePriceMarketReactionData = ({
  entityTag,
  ticker,
  reportingDate,
  enabled = true,
  isMultipleEntitiesChart = false,
  variant = 'earnings',
  isMarketCatalyst = false,
}: UsePriceMarketReactionDataProps) => {
  const reportingDateET = toZonedTime(
    new Date(reportingDate),
    NEW_YORK_TIMEZONE
  );
  const {
    isPreMarket: isReportingPreMarket,
    isAfterMarket: isReportingAfterMarket,
  } = getMarketHours(new Date(reportingDate));
  const { fromDateUTC, toDateUTC } = useMarketReactionDates(reportingDate);

  const { data: priceHistoryData, isLoading: isPriceHistoryLoading } =
    usePriceHistory({
      entityTag,
      ticker,
      resolution: '1m',
      dateFrom: fromDateUTC.toISOString(),
      dateTo: toDateUTC.toISOString(),
      enabled,
    });

  const { data: prevCloseHistoryData, isLoading: isPrevCloseLoading } =
    usePriceHistory({
      entityTag,
      ticker,
      resolution: '1D',
      dateFrom: addDays(fromDateUTC, -5).toISOString(),
      dateTo: addDays(toDateUTC, 4).toISOString(),
      enabled: !isMultipleEntitiesChart,
    });

  const isLoading = isPriceHistoryLoading || isPrevCloseLoading;

  const priceByDay = useMemo(() => {
    if (!Array.isArray(priceHistoryData)) {
      if (priceHistoryData !== undefined) {
        Tracking.captureException(
          new Error(
            'usePriceMarketReactionData: priceHistoryData is not an array'
          ),
          {
            entityTag,
            ticker,
            reportingDate,
            dataType: typeof priceHistoryData,
            dataValue: JSON.stringify(priceHistoryData)?.slice(0, 200),
          }
        );
      }

      return undefined;
    }

    return priceHistoryData.reduce<PriceByDay>((acc, point) => {
      const zonedDate = toZonedTime(new Date(point.time), NEW_YORK_TIMEZONE);
      const day = format(zonedDate, DATE_FORMAT);

      if (!acc[day]) {
        acc[day] = [];
      }

      acc[day].push({ time: point.time, value: point.close });

      return acc;
    }, {});
  }, [priceHistoryData]);

  if (!priceByDay || Object.keys(priceByDay).length === 0) {
    return {
      priceData: [],
      fromDateEST: undefined,
      toDateEST: undefined,
      lastProportionChange: undefined,
      prevCloseData: undefined,
      lastKnownPrice: undefined,
      lastKnownDate: undefined,
      isLoading,
    };
  }

  const days = Object.keys(priceByDay);

  if (variant === 'catalyst') {
    return getCatalystData({
      days,
      priceByDay,
      reportingDateET,
      isReportingPreMarket,
      isReportingAfterMarket,
      prevCloseHistoryData,
      isLoading,
      isMarketCatalyst,
    });
  }

  return getEarningsData({
    days,
    priceByDay,
    reportingDateET,
    isReportingPreMarket,
    prevCloseHistoryData,
    isLoading,
  });
};
